import { useState } from "react";

import { useTrades } from "../api/queries";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { adena, plural, signed, when } from "../format";
import type { TradeRow } from "../api/types";

/** The log is an event trail, not a ledger of positions: a deal that went
 *  wrong leaves the same row shape as one that went right, and says where it
 *  stopped. `bought` and `sold` are the two that moved money. */
const EVENTS: Record<string, string> = {
  found: "found",
  confirmed: "confirmed",
  bought: "bought",
  sold: "sold",
  aborted: "aborted",
  held: "held",
};

function withWhom(row: TradeRow): string {
  if (!row.counterparty) return "";
  return row.side === "seller" ? `from ${row.counterparty}` : `to ${row.counterparty}`;
}

/** The adena either side of the click — the only number that cannot be
 *  argued with. The client's own price is what the game meant to charge; this
 *  is what actually moved. */
function moved(row: TradeRow): string | null {
  if (row.adena_before === null || row.adena_after === null) return null;
  return signed(row.adena_after - row.adena_before);
}

export function Trades() {
  const [liveOnly, setLiveOnly] = useState(false);
  const trades = useTrades(liveOnly);

  if (trades.isLoading) {
    return (
      <>
        <TitleBand title="Deal hunter" />
        <Skeleton rows={6} />
      </>
    );
  }

  if (trades.isError) {
    // 403 is an ordinary answer, not a fault: the hunt is behind a per-user
    // feature and most accounts have not been given it.
    const status = (trades.error as { status?: number } | null)?.status;
    return (
      <>
        <TitleBand title="Deal hunter" />
        <Panel primary>
          {status === 403 ? (
            <Empty>
              The deal hunter is not enabled for this account. It is being rolled out one
              account at a time — ask an owner to turn it on.
            </Empty>
          ) : (
            <ErrorBand
              message="Could not load the trades."
              hint="The server may be restarting. Reload in a moment."
            />
          )}
        </Panel>
      </>
    );
  }

  const totals = trades.data?.totals;
  const rows = trades.data?.trades ?? [];
  const holdings = trades.data?.holdings ?? [];
  const orders = trades.data?.orders ?? [];

  return (
    <>
      <TitleBand
        title="Deal hunter"
        note={totals ? plural(totals.deals, "deal") : undefined}
      />

      {totals && (
        <Panel>
          <dl className="cost-summary">
            <div>
              <dt>Profit</dt>
              <dd className={totals.profit >= 0 ? "buy" : "sell"}>{adena(totals.profit)}</dd>
            </div>
            <div><dt>Bought</dt><dd>{totals.bought}</dd></div>
            <div><dt>Sold</dt><dd>{totals.sold}</dd></div>
            <div><dt>Aborted</dt><dd>{totals.aborted}</dd></div>
            {/* Counted apart and never added in: a rehearsal's numbers are
                what would have happened, and mixing them in makes the total
                mean nothing. */}
            <div><dt>Rehearsed</dt><dd>{totals.rehearsed_deals}</dd></div>
          </dl>
        </Panel>
      )}

      {orders.length > 0 && (
        <Panel primary>
          <p className="scan-meta">
            {plural(orders.length, "standing order")} — what the hunt will sell as it walks.
            Each is filled by the highest bidder at or above its price, a little at a time:
            the top bid is rarely for the whole lot.
          </p>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">To sell</th>
                  <th className="num">Sold</th>
                  <th className="num">At least</th>
                  <th>Standing since</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.uid}>
                    <td>{order.item}</td>
                    {/* No number means all of it, until it is called off. */}
                    <td className="num">{order.qty === null ? "all" : order.qty}</td>
                    <td className="num">{order.sold}</td>
                    <td className="num">{adena(order.min_price)}</td>
                    <td className="dim">{when(order.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {holdings.length > 0 && (
        <Panel primary>
          <p className="scan-meta">
            Holding {plural(holdings.length, "lot")} — bought and not sold on.
            A leg that cannot sell at or above what it paid keeps the stock rather than taking a
            loss to clear it.
          </p>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th className="num">Paid each</th>
                  <th className="num">Cost</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((held) => (
                  <tr key={`${held.trade_uid}-${held.item}-${held.acquired_at}`}>
                    <td>{held.item}</td>
                    <td className="num">{held.qty}</td>
                    <td className="num">{adena(held.unit_cost)}</td>
                    <td className="num">{adena(held.qty * held.unit_cost)}</td>
                    <td className="dim">{when(held.acquired_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <Panel primary>
        <div className="strip">
          <span>Every step</span>
          <span className="spacer" />
          <label className="trade-filter">
            <input
              type="checkbox"
              checked={liveOnly}
              onChange={(event) => setLiveOnly(event.target.checked)}
            />
            Hide rehearsals
          </label>
        </div>
        {rows.length === 0 ? (
          <Empty>
            Nothing yet. Arm the hunt in the market window — it records every step, including
            the ones that stopped.
          </Empty>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Event</th>
                  <th>Item</th>
                  <th className="num">Qty</th>
                  <th className="num">Price</th>
                  <th>With</th>
                  <th className="num">Adena</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.trade_uid}-${row.leg}-${row.event}-${row.t}`}>
                    <td className="dim">{when(row.t)}</td>
                    <td className={`trade-${row.event}`}>
                      {EVENTS[row.event] ?? row.event}
                      {row.dry_run && <span className="dim"> dry</span>}
                    </td>
                    <td>{row.item || <span className="dim">—</span>}</td>
                    <td className="num">{row.qty ?? ""}</td>
                    <td className="num">{row.unit_price === null ? "" : adena(row.unit_price)}</td>
                    <td className="dim">{withWhom(row)}</td>
                    <td className="num">{moved(row) ?? ""}</td>
                    <td className="dim">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
